import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type TargetDataType = 'trial' | 'percent' | 'frequency' | 'duration' | 'task_analysis';
export type MasteryDirection = 'at_or_above' | 'at_or_below';
export type ChainingMethod = 'total_task' | 'forward' | 'backward';

export const TEACHING_METHODS = [
    'Discrete Trial Training (DTT)',
    'Natural Environment Training (NET)',
    'Behavioral Skills Training (BST)',
    'Task Analysis / Chaining',
    'Incidental Teaching',
    'PECS',
    'Other',
] as const;

export interface TargetBankEntry {
    id: string;
    programBankId: string;
    name: string;
    description: string;
    dataType: TargetDataType;
    trials: number | null;
    taSteps: unknown[] | null;
    chainingMethod: ChainingMethod | null;
    masteryDirection: MasteryDirection;
    masteryValue: number | null;
    masterySessions: number | null;
    sortOrder: number;
    generalization: string;
    baselineDate: string | null;
    baseline: string;
    archived: boolean;
    archivedAt: string | null;
}

export interface ProgramBankEntry {
    id: string;
    programDomainId: string | null;
    programDomain: string;
    name: string;
    teachingMethod: string;
    description: string;
    archived: boolean;
    archivedAt: string | null;
    targets?: TargetBankEntry[];
}

export interface ProgramBankWritePayload {
    name?: string;
    programDomainId?: string | null;
    teachingMethod?: string;
    description?: string;
}

export interface TargetBankWritePayload {
    name?: string;
    description?: string;
    dataType?: TargetDataType;
    trials?: number | null;
    taSteps?: unknown[] | null;
    chainingMethod?: ChainingMethod | null;
    masteryDirection?: MasteryDirection;
    masteryValue?: number | null;
    masterySessions?: number | null;
    sortOrder?: number;
    generalization?: string;
    baselineDate?: string | null;
    baseline?: string;
}

interface ListOpts {
    programDomainId?: string;
    includeTargets?: boolean;
    includeArchived?: boolean;
}

async function fetchProgramBank(opts: ListOpts = {}): Promise<ProgramBankEntry[]> {
    const params: Record<string, string> = {};
    if (opts.programDomainId) params.programDomainId = opts.programDomainId;
    if (opts.includeTargets) params.includeTargets = 'true';
    if (opts.includeArchived) params.includeArchived = '1';
    const res = await api.get<ProgramBankEntry[]>('/program-bank', { params });
    return res.data;
}

export function useProgramBank(opts: ListOpts = {}) {
    return useQuery({
        queryKey: [
            'program-bank',
            opts.programDomainId ?? 'all',
            opts.includeTargets ? 'with-targets' : 'headers',
            opts.includeArchived ? 'with-archived' : 'active',
        ] as const,
        queryFn: () => fetchProgramBank(opts),
        staleTime: 60_000,
    });
}

async function fetchProgramBankEntry(id: string, includeArchived?: boolean): Promise<ProgramBankEntry> {
    const res = await api.get<ProgramBankEntry>(`/program-bank/${id}`, {
        params: includeArchived ? { includeArchived: '1' } : undefined,
    });
    return res.data;
}

export function useProgramBankEntry(id: string | undefined, opts: { includeArchived?: boolean } = {}) {
    return useQuery({
        queryKey: ['program-bank', 'entry', id, opts.includeArchived ? 'with-archived' : 'active'] as const,
        queryFn: () => fetchProgramBankEntry(id!, opts.includeArchived),
        enabled: !!id,
    });
}

// ─── program mutations ──────────────────────────────────────────────────────

export function useCreateProgramBankEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ProgramBankWritePayload) => {
            const res = await api.post<ProgramBankEntry>('/program-bank', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['program-bank'] }),
    });
}

export function useUpdateProgramBankEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ProgramBankWritePayload }) => {
            const res = await api.patch<ProgramBankEntry>(`/program-bank/${id}`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['program-bank'] });
            qc.invalidateQueries({ queryKey: ['program-bank', 'entry', vars.id] });
        },
    });
}

export function useDeleteProgramBankEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/program-bank/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['program-bank'] }),
    });
}

export function useRestoreProgramBankEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const res = await api.post<ProgramBankEntry>(`/program-bank/${id}/restore`);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['program-bank'] }),
    });
}

export function useCopyProgramBankToClient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, clientId }: { id: string; clientId: string }) => {
            const res = await api.post<{ id: string; targetCount: number }>(
                `/program-bank/${id}/copy-to-client/${clientId}`,
            );
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['client-programs', vars.clientId] });
        },
    });
}

// ─── target mutations ───────────────────────────────────────────────────────

export function useCreateTarget() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ programId, payload }: { programId: string; payload: TargetBankWritePayload }) => {
            const res = await api.post<TargetBankEntry>(`/program-bank/${programId}/targets`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['program-bank'] });
            qc.invalidateQueries({ queryKey: ['program-bank', 'entry', vars.programId] });
        },
    });
}

export function useUpdateTarget() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({
            programId,
            targetId,
            payload,
        }: {
            programId: string;
            targetId: string;
            payload: TargetBankWritePayload;
        }) => {
            const res = await api.patch<TargetBankEntry>(
                `/program-bank/${programId}/targets/${targetId}`,
                payload,
            );
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['program-bank'] });
            qc.invalidateQueries({ queryKey: ['program-bank', 'entry', vars.programId] });
        },
    });
}

export function useDeleteTarget() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ programId, targetId }: { programId: string; targetId: string }) => {
            await api.delete(`/program-bank/${programId}/targets/${targetId}`);
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['program-bank'] });
            qc.invalidateQueries({ queryKey: ['program-bank', 'entry', vars.programId] });
        },
    });
}

/**
 * Reorder active (non-archived) targets for one bank program. `ids` must
 * cover every active target id for the program exactly once — server
 * rewrites each row's sort_order to its position in the array; archived
 * targets keep their stored sort_order. Optimistic update with snapshot
 * rollback on failure.
 */
export function useReorderTargets() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ programId, ids }: { programId: string; ids: string[] }) => {
            const res = await api.put<TargetBankEntry[]>(
                `/program-bank/${programId}/targets/reorder`,
                { ids },
            );
            return res.data;
        },
        onMutate: async ({ programId, ids }) => {
            await qc.cancelQueries({ queryKey: ['program-bank'] });
            const snapshots = qc.getQueriesData<ProgramBankEntry | ProgramBankEntry[]>({
                queryKey: ['program-bank'],
            });
            for (const [key, snap] of snapshots) {
                if (!snap) continue;
                if (Array.isArray(snap)) {
                    const next = snap.map((p) => {
                        if (p.id !== programId || !p.targets) return p;
                        return { ...p, targets: reorderTargets(p.targets, ids) };
                    });
                    qc.setQueryData(key, next);
                } else if (snap.id === programId && snap.targets) {
                    qc.setQueryData(key, {
                        ...snap,
                        targets: reorderTargets(snap.targets, ids),
                    });
                }
            }
            return { snapshots };
        },
        onError: (_err, _vars, ctx) => {
            if (!ctx) return;
            for (const [key, snap] of ctx.snapshots) {
                qc.setQueryData(key, snap);
            }
        },
        onSettled: (_data, _err, vars) => {
            qc.invalidateQueries({ queryKey: ['program-bank'] });
            qc.invalidateQueries({ queryKey: ['program-bank', 'entry', vars.programId] });
        },
    });
}

function reorderTargets(targets: TargetBankEntry[], ids: string[]): TargetBankEntry[] {
    const byId = new Map(targets.map((t) => [t.id, t]));
    const reordered = ids
        .map((id, idx) => {
            const row = byId.get(id);
            return row ? { ...row, sortOrder: idx + 1 } : null;
        })
        .filter((r): r is TargetBankEntry => r !== null);
    const untouched = targets.filter((t) => !ids.includes(t.id));
    return [...reordered, ...untouched];
}

export function useRestoreTarget() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ programId, targetId }: { programId: string; targetId: string }) => {
            const res = await api.post<TargetBankEntry>(
                `/program-bank/${programId}/targets/${targetId}/restore`,
            );
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['program-bank'] });
            qc.invalidateQueries({ queryKey: ['program-bank', 'entry', vars.programId] });
        },
    });
}
